import { Suspense, useMemo, type ReactNode } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { SoftShadows } from '@react-three/drei'
import { Vloer } from './Vloer'
import { Stellingen } from './Stellingen'
import { Lading } from './Lading'
import { Licht } from './Licht'
import { Dak } from './Dak'
import { Armaturen } from './Armaturen'
import { Omgeving } from './Omgeving'
import { Plafond } from './Plafond'
import { Props } from './Props'
import { Installaties } from './Installaties'
import { Stof } from './Stof'
import { Matic } from './trucks'
import { Hotspot } from './Hotspot'
import { CameraRig } from './CameraRig'
import { Effecten } from './Effecten'
import { STANDEN, hotspotStand, useIndeling } from './indeling'
import { PROEF } from './proef'
import { useLindeBeeld } from './trucks/LindeBeeld'
import { maakWerkgangen, werkvakken } from './werkgangen'
import { useMerkTokens } from '../kiosk/useMerkTokens'
import { useKiosk } from '../state/store'
import { useAnimatie, useLicht, useObjecten, useOppervlak } from '../scene/store'
import { HOTSPOTS, t } from '../content/content'
import { halklok } from './omloop'

/**
 * Telt de klok van de hal op (`omloop.ts`), vóór alles wat erop loopt: een
 * negatieve prioriteit, dus eerder dan elke andere `useFrame` en zonder dat
 * R3F het tekenen uit handen geeft (dat doet hij alleen bij een positieve).
 */
function Halklok() {
  const anim = useAnimatie()
  useFrame((_, delta) => {
    if (halklok.extern) return
    if (PROEF.tijd !== null) halklok.t = PROEF.tijd
    else if (anim.aan) halklok.t += Math.min(delta, 0.1) * anim.snelheid
  }, -10)
  return null
}

/**
 * Het magazijn. Eén scène voor alle vijf onderdelen: wat verandert is de laag
 * die erover ligt, niet de ruimte zelf.
 *
 * `children` komen binnen de Suspense-grens te staan, niet ernaast. Dat is het
 * verschil tussen "de scène is gemonteerd" en "de scène is er": alles hieronder
 * hangt aan één `<Suspense>`, dus een kind ernaast zou al tekenen terwijl de hal
 * nog `null` is. Het laadscherm in App.tsx hangt daaraan.
 *
 * `machines`: de Matics, de C-MATIC-pendel, de schrobmachine en de
 * wikkelaar. Sinds 22 oktober overal uit, ook in de onderdelen ("enkel het
 * magazijn"; de machines worden los verbeterd en komen misschien later
 * terug): de schakelaar `objecten.machines` in de Studio zet ze weer aan.
 * Uit is niet gemonteerd: geen Linde-beelden laden, geen werk per beeld. De
 * hallfilm zet ze vast aan: die draait om de Matics.
 */
export function Magazijn({ children, machines: vast }: { children?: ReactNode; machines?: boolean }) {
  const tokens = useMerkTokens()
  const ind = useIndeling()
  const licht = useLicht()
  const obj = useObjecten()
  const opp = useOppervlak()

  const scherm = useKiosk((s) => s.scherm)
  const taal = useKiosk((s) => s.taal)
  const hotspot = useKiosk((s) => s.hotspot)
  const kiesHotspot = useKiosk((s) => s.kiesHotspot)

  const machines = vast ?? obj.machines
  const inAutomatisering = scherm === 'automatisering'
  const gekozen = inAutomatisering ? HOTSPOTS.find((h) => h.id === hotspot) : undefined
  // Eén object per standpunt, niet per render: CameraRig zet bij elke nieuwe
  // stand het transitieslot van 1,4 s. Zonder useMemo gaf elke rerender met
  // een hotspot open — de productviewer sluiten, de taal wisselen — een vlucht
  // naar dezelfde plek en 1,4 s knoppen die een tik negeerden.
  const stand = useMemo(
    () => PROEF.cam ?? (gekozen ? hotspotStand(ind, gekozen.x, gekozen.gangpad) : STANDEN[scherm] ?? STANDEN.hoofdscherm),
    [gekozen, ind, scherm],
  )

  // Eén Matic per hotspot; de K- en R-MATIC halen en brengen pallets in
  // vakken die `Lading` voor ze vrijhoudt (zie `werkgangen.ts`).
  const trucks = useMemo(() => maakWerkgangen(ind, obj.trucks), [ind, obj.trucks])
  // Als Linde-beeld halen de K- en R-MATIC niets uit de stelling: dan staat hij
  // vol. Zonder machines ook: hun pallets in de stelling verdwijnen met ze.
  const linde = useLindeBeeld()
  const vrij = useMemo(() => (linde || !machines ? [] : werkvakken(trucks)), [trucks, linde, machines])

  return (
    <>
      {/*
        PCSS: de schaduwrand wordt zachter naarmate het object verder van de
        vloer staat. Dat is wat een schaduw als contact laat lezen — een pallet
        raakt de vloer en heeft dus een harde rand, een ligger op vier meter
        hoogte niet. De shadow map van 2048 over 80 m geeft 4 cm per texel, en
        een harde rand daarop is precies de trap die je nu onder elke staander
        ziet.

        `samples` staat op 8 en niet op de standaard 10: dit zijn 16 extra
        textuurbemonsteringen per schaduwpixel over een scène die het hele beeld
        vult, en de beurs-pc is nog niet gekozen (PLAN.md §2.5). Uit kan altijd.
      */}
      {opp.zachteSchaduwen && licht.schaduwen && <SoftShadows size={16} samples={8} focus={0.6} />}

      <color attach="background" args={[licht.mistKleur]} />
      <fog attach="fog" args={[licht.mistKleur, licht.mistNabij, licht.mistVer]} />

      <Halklok />
      <CameraRig stand={stand} zweef={!PROEF.cam && scherm === 'hoofdscherm'} />

      <Suspense fallback={null}>
        <Omgeving />
        <Licht />
        <Dak />
        <Armaturen />
        <Vloer />
        <Stellingen />
        <Lading vrij={vrij} />
        <Plafond />
        <Props />
        <Installaties machines={machines} />
        <Stof />

        {machines && trucks.map((tr, i) => (
          <Matic key={i} tokens={tokens} {...tr} />
        ))}

        {inAutomatisering &&
          HOTSPOTS.map((h) => (
            <Hotspot
              key={h.id}
              positie={[h.x, 2.6, ind.gangpadZ(h.gangpad)]}
              label={t(h.oplossing, taal)}
              actief={hotspot === h.id}
              tokens={tokens}
              onKies={() => kiesHotspot(h.id)}
            />
          ))}

        {children}
      </Suspense>

      <Effecten />
    </>
  )
}

/** Renderinstellingen die bij de kiosk horen, niet bij een willekeurige browser. */
export const CANVAS_INSTELLINGEN = {
  shadows: true,
  dpr: [1, 2] as [number, number],
  gl: {
    antialias: false, // SMAA doet dit in de postprocessing-keten
    powerPreference: 'high-performance' as const,
    // Geldt alleen zolang er geen composer is: de EffectComposer zet dit op
    // NoToneMapping, en de tone mapping (AgX) zit in `film/FilmLook.ts`. Wat
    // hier staat zie je dus alleen in het ene frame waarin de Studio de
    // composer opnieuw opbouwt.
    toneMapping: THREE.ACESFilmicToneMapping,
    toneMappingExposure: 1.05,
  },
  camera: { fov: 42, near: 0.1, far: 220, position: [33, 3.6, 4.3] as [number, number, number] },
}
